/**
 * Shared helpers for the MindAR marker image pipeline — used both by the
 * AR Preview section (to stage a selected image) and by the submit
 * pipeline in FormView (to run Rekognition, compile a .mind target, and
 * upload both as part of campaign creation).
 *
 * Extracted so marker work no longer has to live inside the preview
 * component and can execute cleanly at submit time when the user is
 * guaranteed to be authenticated.
 */

const DEFAULT_MAX_SIDE = 1024

export const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Failed to load image'))
    img.src = URL.createObjectURL(file)
  })

/**
 * If the image is already within `maxSide` on its longest edge, return it
 * as-is. Otherwise render it into a canvas and produce a new
 * HTMLImageElement at the reduced size. Aspect ratio preserved.
 */
export const downscaleForCompile = async (
  img: HTMLImageElement,
  maxSide: number = DEFAULT_MAX_SIDE,
): Promise<HTMLImageElement> => {
  const longest = Math.max(img.width, img.height)
  if (longest <= maxSide) return img

  const ratio = maxSide / longest
  const targetW = Math.max(1, Math.round(img.width * ratio))
  const targetH = Math.max(1, Math.round(img.height * ratio))

  const canvas = document.createElement('canvas')
  canvas.width = targetW
  canvas.height = targetH
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Could not get 2D context for image downscale')
  ctx.drawImage(img, 0, 0, targetW, targetH)

  const dataUrl = canvas.toDataURL('image/jpeg', 0.92)
  return new Promise((resolve, reject) => {
    const resized = new Image()
    resized.onload = () => resolve(resized)
    resized.onerror = () => reject(new Error('Failed to load downscaled marker image'))
    resized.src = dataUrl
  })
}

/**
 * Re-encode an HTMLImageElement (typically the downscaled output above)
 * into a JPEG File suitable for upload. The upload always carries the
 * downscaled version — original 12MP iPhone captures blew up the
 * upload + Rekognition + compile chain.
 */
export const imageElementToJpegFile = async (
  img: HTMLImageElement,
  filename: string,
): Promise<File> => {
  const canvas = document.createElement('canvas')
  canvas.width = img.width
  canvas.height = img.height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Could not get 2D context to encode marker image')
  ctx.drawImage(img, 0, 0)
  const blob: Blob | null = await new Promise((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.92),
  )
  if (!blob) throw new Error('Failed to encode downscaled marker image as JPEG')
  return new File([blob], filename, { type: 'image/jpeg' })
}

/**
 * One-shot prep for a raw marker File. Loads it, downscales if needed,
 * returns both the encoded JPEG File (for upload) and the image element
 * (so the MindAR compile can run from the same pixels without a
 * re-decode).
 */
export const prepareMarkerUpload = async (
  file: File,
  timestamp: number = Date.now(),
): Promise<{ file: File; image: HTMLImageElement }> => {
  const sanitized = file.name
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/\.(heic|heif|png|webp|gif|bmp|tiff?)$/i, '.jpg')
  const original = await loadImage(file)
  const resized = await downscaleForCompile(original, DEFAULT_MAX_SIDE)
  const jpegFile = await imageElementToJpegFile(
    resized,
    `marker-source-${timestamp}-${sanitized}`,
  )
  return { file: jpegFile, image: resized }
}

/**
 * Compile a MindAR .mind target blob from the given HTMLImageElement.
 * `onProgress` receives a 0–100 integer. Dynamic import keeps the heavy
 * mind-ar bundle out of the main chunk.
 */
export const compileMindBlob = async (
  image: HTMLImageElement,
  onProgress?: (progress: number) => void,
): Promise<Blob> => {
  const { Compiler } = await import('mind-ar/src/image-target/compiler.js')
  const compiler = new Compiler()
  await compiler.compileImageTargets([image], (progress: number) => {
    onProgress?.(Math.round(progress))
  })
  const buffer = compiler.exportData()
  return new Blob([buffer], { type: 'application/octet-stream' })
}
